import React, { useState, useEffect } from "react";
import { X, UploadCloud, ArrowRight, CheckCircle } from "lucide-react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { commitImport, listCostDefinitions, listFlagDefinitions, listFieldDefinitions } from "../../services/catalogService";
import ColumnMappingRow from "./ColumnMappingRow";

const STEPS = ["Upload", "Key Column", "Map Columns", "Review"];

const AdvancedImportModal = ({ marketplace = "walmart", onClose, onImported }) => {
    const [step, setStep] = useState(0);
    const [filename, setFilename] = useState("");
    const [headers, setHeaders] = useState([]);
    const [rows, setRows] = useState([]);
    const [keyColumn, setKeyColumn] = useState("");
    const [keyType, setKeyType] = useState("sku");
    const [mapping, setMapping] = useState({});
    const [costDefinitions, setCostDefinitions] = useState([]);
    const [flagDefinitions, setFlagDefinitions] = useState([]);
    const [fieldDefinitions, setFieldDefinitions] = useState([]);
    const [committing, setCommitting] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        listCostDefinitions().then((d) => setCostDefinitions(d.definitions || []));
        listFlagDefinitions().then((d) => setFlagDefinitions(d.definitions || []));
        listFieldDefinitions().then((d) => setFieldDefinitions(d.definitions || []));
    }, []);

    const handleFile = (file) => {
        setFilename(file.name);
        const isExcel = /\.xlsx?$/i.test(file.name);

        if (isExcel) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const wb = XLSX.read(e.target.result, { type: "binary" });
                const sheet = wb.Sheets[wb.SheetNames[0]];
                const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });
                if (json.length > 0) {
                    setHeaders(Object.keys(json[0]));
                    setRows(json);
                }
            };
            reader.readAsBinaryString(file);
        } else {
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: (res) => {
                    setHeaders(res.meta.fields || []);
                    setRows(res.data);
                },
            });
        }
    };

    const setColumnMapping = (header, cfg) => setMapping((m) => ({ ...m, [header]: cfg }));

    const handleCommit = async () => {
        setCommitting(true);
        setError("");
        try {
            const cleanMapping = { ...mapping };
            delete cleanMapping[keyColumn];
            const res = await commitImport({
                marketplace,
                filename,
                key_column: keyColumn,
                key_type: keyType,
                mapping: cleanMapping,
                rows,
            });
            setResult(res);
            onImported && onImported(res);
        } catch (e) {
            setError(e.message || "Import failed");
        } finally {
            setCommitting(false);
        }
    };

    const mappableHeaders = headers.filter((h) => h !== keyColumn);

    return (
        <div className="fixed inset-0 bg-black/60 z-[9999999] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                <div className="bg-gradient-to-r from-teal-500 to-blue-600 px-6 py-4 flex items-center justify-between flex-shrink-0">
                    <div>
                        <h2 className="text-white font-bold text-lg">Advanced Import</h2>
                        <p className="text-teal-100 text-xs mt-0.5">Import costs, flags, or any data from a CSV/Excel file</p>
                    </div>
                    <button onClick={onClose} className="text-white/70 hover:text-white"><X size={20} /></button>
                </div>

                <div className="flex items-center gap-1 px-6 py-3 border-b border-gray-100 flex-shrink-0">
                    {STEPS.map((s, i) => (
                        <React.Fragment key={s}>
                            <span className={`text-xs font-bold px-2 py-1 rounded-full ${i === step ? "bg-teal-100 text-teal-700" : i < step ? "text-green-600" : "text-gray-300"}`}>
                                {i + 1}. {s}
                            </span>
                            {i < STEPS.length - 1 && <ArrowRight size={12} className="text-gray-300" />}
                        </React.Fragment>
                    ))}
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                    {step === 0 && (
                        <div>
                            <label className="block border-2 border-dashed border-gray-300 hover:border-teal-400 rounded-xl p-10 text-center cursor-pointer transition">
                                <UploadCloud size={32} className="mx-auto text-gray-300 mb-2" />
                                <p className="text-sm text-gray-500">Drop a CSV or Excel file here, or click to browse</p>
                                <input type="file" accept=".csv,.xlsx,.xls" className="hidden"
                                    onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])} />
                            </label>
                            {rows.length > 0 && (
                                <p className="text-sm text-green-600 mt-3 flex items-center gap-1">
                                    <CheckCircle size={14} /> Loaded {filename} — {rows.length} rows, {headers.length} columns
                                </p>
                            )}
                        </div>
                    )}

                    {step === 1 && (
                        <div>
                            <label className="text-xs font-semibold text-gray-600 mb-1 block">Which column identifies the product?</label>
                            <select value={keyColumn} onChange={(e) => setKeyColumn(e.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-4">
                                <option value="">Select column...</option>
                                {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                            </select>

                            <label className="text-xs font-semibold text-gray-600 mb-1 block">Column type</label>
                            <div className="flex gap-2">
                                {["sku", "upc", "gtin"].map((t) => (
                                    <button key={t} onClick={() => setKeyType(t)}
                                        className={`px-4 py-2 rounded-lg text-xs font-bold border-2 uppercase transition ${keyType === t ? "border-teal-500 bg-teal-50 text-teal-700" : "border-gray-200 text-gray-500"}`}>
                                        {t}
                                    </button>
                                ))}
                            </div>
                            <p className="text-xs text-gray-400 mt-3">
                                Rows that don't match an existing product still get imported as a new bare catalog entry — sync from the marketplace later to fill in the rest.
                            </p>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-3">
                            {mappableHeaders.map((h) => (
                                <ColumnMappingRow key={h} header={h} sample={rows[0]?.[h]} config={mapping[h]}
                                    costDefinitions={costDefinitions} flagDefinitions={flagDefinitions} fieldDefinitions={fieldDefinitions}
                                    onChange={(cfg) => setColumnMapping(h, cfg)} />
                            ))}
                        </div>
                    )}

                    {step === 3 && (
                        <div>
                            <div className="bg-gray-50 rounded-xl p-4 mb-4 text-sm space-y-1">
                                <p><strong>{rows.length}</strong> rows will be processed</p>
                                <p>Key column: <strong>{keyColumn}</strong> ({keyType.toUpperCase()})</p>
                                <p><strong>{Object.keys(mapping).filter((k) => k !== keyColumn).length}</strong> columns mapped</p>
                            </div>

                            {!result ? (
                                <button onClick={handleCommit} disabled={committing}
                                    className="w-full py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition disabled:opacity-50">
                                    {committing ? "Importing..." : `Import ${rows.length} rows`}
                                </button>
                            ) : (
                                <div>
                                    <div className={`border rounded-xl p-4 text-sm ${result.failed_count > 0 ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-green-50 border-green-200 text-green-700"}`}>
                                        <CheckCircle size={16} className="inline mr-1" />
                                        Imported! {result.matched_count} products matched, {result.fields_written ?? 0} catalog fields updated, {result.costs_written} costs written, {result.flags_written} flags written, {result.custom_fields_written ?? 0} custom fields written, {result.fees_written ?? 0} fee breakdowns updated.
                                        {result.failed_count > 0 && (
                                            <span className="block mt-1 font-semibold">
                                                {result.failed_count} row{result.failed_count > 1 ? "s" : ""} could not be imported and {result.failed_count > 1 ? "were" : "was"} skipped — everything else succeeded.
                                            </span>
                                        )}
                                    </div>
                                    {result.failed_rows?.length > 0 && (
                                        <div className="mt-3 border border-amber-200 rounded-xl overflow-hidden">
                                            <div className="bg-amber-50 px-3 py-2 text-xs font-bold text-amber-700">Skipped rows</div>
                                            <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
                                                {result.failed_rows.map((f, i) => (
                                                    <div key={i} className="px-3 py-2 text-xs flex items-start gap-2">
                                                        <span className="font-mono font-bold text-gray-600 flex-shrink-0">
                                                            {f.key || (f.row_number ? `Row ${f.row_number}` : "Unknown row")}
                                                        </span>
                                                        <span className="text-gray-400">{f.error}</span>
                                                    </div>
                                                ))}
                                            </div>
                                            {result.failed_count > result.failed_rows.length && (
                                                <p className="px-3 py-1.5 text-[11px] text-amber-600 bg-amber-50">
                                                    + {result.failed_count - result.failed_rows.length} more not shown
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                            {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 flex-shrink-0">
                    <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
                        className="px-4 py-2 text-sm text-gray-500 disabled:opacity-30">Back</button>
                    {step < 3 ? (
                        <button onClick={() => setStep((s) => s + 1)}
                            disabled={(step === 0 && rows.length === 0) || (step === 1 && !keyColumn)}
                            className="px-6 py-2 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition disabled:opacity-40 flex items-center gap-1">
                            Next <ArrowRight size={14} />
                        </button>
                    ) : result && (
                        <button onClick={onClose} className="px-6 py-2 bg-gray-800 text-white rounded-xl font-bold hover:bg-gray-700 transition">Done</button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdvancedImportModal;